import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import "./variables.css";
import "./PlayerProgressionResults.css";
import API_URL from "./config";

const PlayerProgressionResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const results = location.state?.results || {
    progressedCount: 0,
    freedCount: 0,
    retiredCount: 0,
    players: [],
    freedAgents: [],
    retiredPlayers: [],
  };

  const [selectedTeam, setSelectedTeam] = useState(null);

  // On mount, fetch user's team and set as default
  useEffect(() => {
    const setDefaultTeam = async () => {
      try {
        const activeSaveId = localStorage.getItem("activeSaveId");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;

        if (!activeSaveId) return;

        const response = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
          headers: { "X-User-ID": userId },
        });

        if (response.ok) {
          const data = await response.json();
          const save = data.save;
          const userTeamId = save?.teamId;

          if (userTeamId) {
            // Find the corresponding team in results (with save_id suffix)
            const userTeamInResults = results.players?.find(
              (p) => p.teamId === userTeamId || p.teamId.includes(userTeamId)
            )?.teamId;

            if (userTeamInResults) {
              setSelectedTeam(userTeamInResults);
            }
          }
        }
      } catch (err) {
        // Error setting default team
      }
    };

    setDefaultTeam();
  }, []);

  // Get unique teams from results (from players, freed agents, and retired players)
  const allProgress = [...(results.players || []), ...(results.freedAgents || []), ...(results.retiredPlayers || [])];
  const teamMap = new Map();
  
  allProgress.forEach((p) => {
    if (p.teamId && !teamMap.has(p.teamId)) {
      teamMap.set(p.teamId, {
        id: p.teamId,
        name: p.teamName || "Unknown",
        abbr: p.teamAbbr || "",
      });
    }
  });

  const teams = Array.from(teamMap.values());
  
  // Sort teams by name
  teams.sort((a, b) => a.name.localeCompare(b.name));

  // Filter players based on selected team
  const displayedPlayers = selectedTeam
    ? results.players.filter((p) => p.teamId === selectedTeam)
    : results.players;

  // Sort players by rating change (largest first)
  const sortedPlayers = [...displayedPlayers].sort(
    (a, b) => Math.abs(b.change) - Math.abs(a.change)
  );

  const handleBack = () => {
    // Advance to draft phase and return to dashboard
    try {
      const activeSaveId = localStorage.getItem("activeSaveId");
      if (activeSaveId) {
        const currentPhase = parseInt(localStorage.getItem(`current_phase_${activeSaveId}`) || "0", 10);
        const nextPhase = currentPhase + 1;
        localStorage.setItem(`current_phase_${activeSaveId}`, nextPhase.toString());
      }
    } catch (err) {
      // Error advancing phase
    }
    navigate("/dashboard");
  };

  return (
    <div>
      <Navbar />
      <div className="progression-results-container">
        <div className="results-header">
          <h1>Player Progression Results</h1>
          <p>Offseason 2025 Development Summary</p>
        </div>

        {/* Summary Stats */}
        <div className="results-stats">
          <div className="stat-card">
            <div className="progression-stat-value">{results.progressedCount}</div>
            <div className="progression-stat-label">Players Progressed</div>
          </div>
          <div className="stat-card">
            <div className="progression-stat-value">{results.freedCount}</div>
            <div className="progression-stat-label">Players to Free Agency</div>
          </div>
          {results.retiredCount > 0 && (
            <div className="stat-card">
              <div className="progression-stat-value">{results.retiredCount}</div>
              <div className="progression-stat-label">Players Retired</div>
            </div>
          )}
          <div className="stat-card">
            <div className="progression-stat-value">
              {results.progressedCount - results.freedCount - (results.retiredCount || 0)}
            </div>
            <div className="progression-stat-label">Roster Remaining</div>
          </div>
        </div>

        {/* Player Changes Table */}
        <div className="results-section">
          <div className="section-header">
            <h2 className="section-title">Roster Changes</h2>
            <div className="team-filter">
              <label htmlFor="team-select">Filter by Team:</label>
              <select
                id="team-select"
                value={selectedTeam || ""}
                onChange={(e) => setSelectedTeam(e.target.value ? e.target.value : null)}
              >
                <option value="">All Teams ({results.players.length})</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({results.players.filter((p) => p.teamId === team.id).length})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="players-table-container">
            <table className="players-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Before</th>
                  <th>After</th>
                  <th>Change</th>
                  <th>Contract</th>
                  <th>Experience</th>
                  <th>Contract</th>
                </tr>
              </thead>
              <tbody>
                {sortedPlayers.map((player, idx) => (
                  <tr key={idx} className={player.freedToAgency ? "freed-row" : ""}>
                    <td className="player-name">{player.name}</td>
                    <td className="ovr-stat">{player.oldOvr}</td>
                    <td className="ovr-stat">{player.newOvr}</td>
                    <td className={`change-stat ${player.change > 0 ? "positive" : player.change < 0 ? "negative" : ""}`}>
                      {player.change > 0 ? "+" : ""}{player.change}
                    </td>
                    <td className="contract-stat">{player.contractYears} yr</td>
                    <td className="experience-stat">{player.experience} yr</td>
                    <td className="status-stat">
                      {player.freedToAgency ? (
                        <span className="status-expired">🚫 Expired</span>
                      ) : (
                        <span className="status-rostered">✓ Rostered</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Freed Agents Section */}
        {results.freedAgents.length > 0 && (
          <div className="results-section freed-agents-section">
            <h2 className="section-title">Freed to Free Agency</h2>
            <div className="freed-agents-list">
              {results.freedAgents
                .filter((agent) => !selectedTeam || agent.teamId === selectedTeam)
                .map((agent, idx) => (
                  <div key={idx} className="freed-agent-card">
                    <div className="agent-header">
                      <div className="agent-name">{agent.name}</div>
                      <div className="agent-team">{agent.teamName}</div>
                    </div>
                    <div className="agent-overall">{agent.overall} OVR</div>
                    <div className="agent-reason">{agent.reason}</div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Retired Players Section */}
        {results.retiredPlayers && results.retiredPlayers.length > 0 && (
          <div className="results-section retired-players-section">
            <h2 className="section-title">Retired Players</h2>
            <div className="retired-players-list">
              {results.retiredPlayers
                .filter((player) => !selectedTeam || player.teamId === selectedTeam)
                .map((player, idx) => (
                  <div key={idx} className="retired-player-card">
                    <div className="player-header">
                      <div className="player-name">{player.name}</div>
                      <div className="player-team">{player.teamName}</div>
                    </div>
                    <div className="player-overall">{player.overall} OVR</div>
                    <div className="player-experience">{player.experience} years experience</div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="results-actions">
          <button className="action-btn back-btn" onClick={handleBack}>
            → Proceed to Draft
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerProgressionResults;
